import React, { useEffect, useState } from 'react';
import { View } from '../../types/view';
import { useProWorkspace } from '../../hooks/useProWorkspace';
import { Lead, ProJob, Quote, JobStatus, ProInvoice } from '../../types/pro';
import { addDays, emptyQuoteLine, hoursOnJob, newLineId, nextJobNo, nextNumber, toISODate } from '../../utils/pro';
import { ProDashboard } from './ProDashboard';
import { ProLeads } from './ProLeads';
import { ProCustomers } from './ProCustomers';
import { ProCustomerDetail } from './ProCustomerDetail';
import { ProQuotes } from './ProQuotes';
import { ProQuoteEditor } from './ProQuoteEditor';
import { ProJobs } from './ProJobs';
import { ProJobDetail } from './ProJobDetail';
import { ProCalendar } from './ProCalendar';
import { ProTime } from './ProTime';
import { ProMaterials } from './ProMaterials';
import { ProInvoices } from './ProInvoices';
import { ProEmployees } from './ProEmployees';
import { ProStatistics } from './ProStatistics';
import { ProSettings } from './ProSettings';
interface ProWorkspaceProps {
  view: View;
  onNavigate: (view: View) => void;
  data: ReturnType<typeof useProWorkspace>;
  /** Record the shell asked us to open, e.g. from global search. */
  selection: {
    kind: 'customer' | 'job' | 'quote' | 'lead';
    id: string;
  } | null;
  onSelectionHandled: () => void;
}

/**
 * Owns the Pro workspace: selection state and the hand-offs between lead,
 * quote, job and invoice. Data is loaded by the shell so search and the
 * quality section can read the same records.
 */
export function ProWorkspace({
  view,
  onNavigate,
  data,
  selection,
  onSelectionHandled
}: ProWorkspaceProps) {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [focusLeadId, setFocusLeadId] = useState<string | undefined>();

  // Global search hands us a record id; adopt it, then clear the request.
  useEffect(() => {
    if (!selection) return;
    if (selection.kind === 'customer') setCustomerId(selection.id);
    if (selection.kind === 'job') setJobId(selection.id);
    if (selection.kind === 'quote') setQuoteId(selection.id);
    if (selection.kind === 'lead') setFocusLeadId(selection.id);
    onSelectionHandled();
  }, [selection, onSelectionHandled]);
  const {
    customers,
    employees,
    leads,
    quotes,
    jobs,
    materials,
    invoices,
    timeEntries,
    settings,
    saveSettings
  } = data;

  // Shared tools live outside this workspace; don't intercept them.
  if (!view.startsWith('pro-')) return null;
  if (data.loading) {
    return <div className="flex min-h-[50vh] items-center justify-center" data-id="element-1573" data-mp="126k6l3o5ekch">
        <p className="text-sm text-stone-500" data-id="element-1574" data-mp="xdqxy4pk6rv4">Henter data…</p>
      </div>;
  }
  const openJob = (id: string) => {
    setJobId(id);
    onNavigate('pro-job-detail');
  };
  const openQuote = (id: string) => {
    setQuoteId(id);
    onNavigate('pro-quote-editor');
  };
  const openCustomer = (id: string) => {
    setCustomerId(id);
    onNavigate('pro-customer-detail');
  };

  // --- Chain: lead -> customer + job -------------------------------------
  const convertLead = async (lead: Lead, alsoCreateJob: boolean) => {
    const newCustomerId = await customers.add({
      customerNo: nextNumber(customers.data.map(c => c.customerNo), 'K'),
      name: lead.name,
      address: lead.address,
      postalCode: '',
      city: lead.city,
      phone: lead.phone,
      email: lead.email,
      notes: lead.notes,
      createdDate: toISODate(new Date()),
      leadSource: lead.source
    });
    if (alsoCreateJob) {
      await jobs.add({
        jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
        customerId: newCustomerId,
        customerName: lead.name,
        address: lead.address,
        city: lead.city,
        title: lead.jobType,
        description: lead.description,
        status: 'ikke-planlagt',
        durationHours: 4,
        employeeIds: lead.ownerId ? [lead.ownerId] : [],
        estimatedHours: 4,
        materials: [],
        photos: [],
        documents: [],
        notes: ''
      });
    }
    if (lead.id) {
      await leads.update(lead.id, {
        stage: 'vundet',
        customerId: newCustomerId,
        lastActivityDate: toISODate(new Date()),
        activity: [...lead.activity, {
          id: `a-${Date.now()}`,
          date: toISODate(new Date()),
          text: alsoCreateJob ? 'Vundet — oprettet som kunde og opgave.' : 'Vundet — oprettet som kunde.',
          author: 'Dig'
        }]
      });
    }
  };

  // --- Chain: lead -> quote ----------------------------------------------
  const quoteFromLead = async (lead: Lead) => {
    const existing = customers.data.find(c => c.name === lead.name);
    const id = await quotes.add({
      quoteNo: nextNumber(quotes.data.map(q => q.quoteNo), 'T'),
      customerId: existing?.id ?? '',
      customerName: lead.name,
      title: lead.jobType,
      status: 'kladde',
      createdDate: toISODate(new Date()),
      validUntil: toISODate(addDays(new Date(), settings.quoteValidDays)),
      lines: [emptyQuoteLine()],
      notes: lead.description,
      leadId: lead.id
    });
    openQuote(id);
  };

  // --- Chain: quote -> job ------------------------------------------------
  const jobFromQuote = async (quote: Quote) => {
    const customer = customers.data.find(c => c.id === quote.customerId);
    const id = await jobs.add({
      jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
      customerId: quote.customerId,
      customerName: quote.customerName,
      address: customer?.address ?? '',
      city: customer?.city ?? '',
      title: quote.title,
      description: quote.notes,
      status: 'ikke-planlagt',
      durationHours: 4,
      employeeIds: [],
      estimatedHours: quote.lines.filter(line => line.unit.startsWith('time')).reduce((sum, line) => sum + line.quantity, 0),
      materials: quote.lines.filter(line => line.materialId).map(line => {
        const material = materials.data.find(m => m.id === line.materialId);
        return {
          id: newLineId(),
          materialId: line.materialId,
          name: line.description,
          itemNo: material?.itemNo ?? '—',
          quantity: line.quantity,
          costPrice: material?.costPrice ?? 0,
          salesPrice: line.unitPrice
        };
      }),
      photos: [],
      documents: [],
      notes: '',
      quoteId: quote.id
    });
    if (quote.id) await quotes.update(quote.id, {
      jobId: id
    });
    openJob(id);
  };

  // --- Chain: job -> invoice ----------------------------------------------
  const invoiceFromJob = async (job: ProJob) => {
    const hours = hoursOnJob(timeEntries.data, job.id ?? '');
    const lines: ProInvoice['lines'] = [];
    if (hours > 0) {
      lines.push({
        id: newLineId(),
        description: 'Arbejdsløn',
        quantity: hours,
        unit: 'timer',
        unitPrice: typeof job.hourlyRate === 'number' ? job.hourlyRate : settings.hourlyRate
      });
    }
    job.materials.forEach(material => lines.push({
      id: newLineId(),
      description: material.name,
      quantity: material.quantity,
      unit: 'stk',
      unitPrice: material.salesPrice
    }));
    const id = await invoices.add({
      invoiceNo: nextNumber(invoices.data.map(i => i.invoiceNo), 'F'),
      customerId: job.customerId,
      customerName: job.customerName,
      jobId: job.id,
      status: 'kladde',
      invoiceDate: toISODate(new Date()),
      dueDate: toISODate(addDays(new Date(), settings.paymentTermsDays)),
      lines,
      notes: ''
    });
    if (job.id) await jobs.update(job.id, {
      status: 'faktureret',
      invoiceId: id
    });
    onNavigate('pro-invoices');
  };
  const createJobAt = async (start: Date, employeeId: string) => {
    const id = await jobs.add({
      jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
      customerId: '',
      customerName: 'Ny aftale',
      address: '',
      city: '',
      title: 'Ny aftale',
      description: '',
      status: 'planlagt',
      start: start.toISOString(),
      durationHours: 2,
      employeeIds: employeeId ? [employeeId] : [],
      estimatedHours: 2,
      materials: [],
      photos: [],
      documents: [],
      notes: ''
    });
    openJob(id);
  };
  const job = jobs.data.find(item => item.id === jobId);
  const quote = quotes.data.find(item => item.id === quoteId);
  const customer = customers.data.find(item => item.id === customerId);
  switch (view) {
    case 'pro-dashboard':
      return <ProDashboard leads={leads.data} quotes={quotes.data} jobs={jobs.data} invoices={invoices.data} employees={employees.data} timeEntries={timeEntries.data} onOpenJob={openJob} onOpenLead={id => {
        setFocusLeadId(id);
        onNavigate('pro-leads');
      }} onNavigate={target => onNavigate(target === 'leads' ? 'pro-leads' : target === 'jobs' ? 'pro-jobs' : target === 'invoices' ? 'pro-invoices' : 'pro-calendar')} onStartJob={item => item.id && jobs.update(item.id, {
        status: 'i-gang'
      })} data-id="element-1575" data-mp="36a283ab9tloz" />;
    case 'pro-leads':
      return <ProLeads leads={leads.data} employees={employees.data} onUpdate={(id, changes) => leads.update(id, changes)} onCreate={lead => leads.add(lead)} onConvert={convertLead} onMakeQuote={quoteFromLead} focusLeadId={focusLeadId} onClearFocus={() => setFocusLeadId(undefined)} data-id="element-1576" data-mp="1cqaby87o0a2t" />;
    case 'pro-customers':
      return <ProCustomers customers={customers.data} jobs={jobs.data} onOpen={openCustomer} data-id="element-1577" data-mp="s3rxqt728q3y" />;
    case 'pro-customer-detail':
      return customer ? <ProCustomerDetail customer={customer} jobs={jobs.data} quotes={quotes.data} invoices={invoices.data} timeEntries={timeEntries.data} employees={employees.data} onBack={() => onNavigate('pro-customers')} onOpenJob={openJob} onOpenQuote={openQuote} onUpdate={changes => customer.id && customers.update(customer.id, changes)} data-id="element-1578" data-mp="1230mn7n2jejg" /> : null;
    case 'pro-quotes':
      return <ProQuotes quotes={quotes.data} vatRate={settings.vatRate} onOpen={openQuote} onNew={async () => {
        const id = await quotes.add({
          quoteNo: nextNumber(quotes.data.map(q => q.quoteNo), 'T'),
          customerId: '',
          customerName: '',
          title: '',
          status: 'kladde',
          createdDate: toISODate(new Date()),
          validUntil: toISODate(addDays(new Date(), settings.quoteValidDays)),
          lines: [emptyQuoteLine()],
          notes: ''
        });
        openQuote(id);
      }} data-id="element-1579" data-mp="3hskeihsg620e" />;
    case 'pro-quote-editor':
      return quote ? <ProQuoteEditor quote={quote} customers={customers.data} materials={materials.data} company={settings} onBack={() => onNavigate('pro-quotes')} onChange={changes => quote.id && quotes.update(quote.id, changes)} onCreateJob={jobFromQuote} data-id="element-1580" data-mp="1c7pd1m977ova" /> : null;
    case 'pro-jobs':
      return <ProJobs jobs={jobs.data} employees={employees.data} timeEntries={timeEntries.data} onOpen={openJob} onSetStatus={(item, status: JobStatus) => item.id && jobs.update(item.id, {
        status
      })} data-id="element-1581" data-mp="nlt61duw8b9p" />;
    case 'pro-job-detail':
      return job ? <ProJobDetail job={job} employees={employees.data} materials={materials.data} timeEntries={timeEntries.data} companyHourlyRate={settings.hourlyRate} onBack={() => onNavigate('pro-jobs')} onUpdate={changes => job.id && jobs.update(job.id, changes)} onAddTime={entry => timeEntries.add(entry)} onUpdateTime={(id, changes) => timeEntries.update(id, changes)} onRemoveTime={id => timeEntries.remove(id)} onCreateInvoice={invoiceFromJob} data-id="element-1582" data-mp="ipb22g7e0gyf" /> : null;
    case 'pro-calendar':
      return <ProCalendar jobs={jobs.data} employees={employees.data} onOpenJob={openJob} onUpdateJob={(id, changes) => jobs.update(id, changes)} onCreateAt={createJobAt} data-id="element-1583" data-mp="3essegqcp8e8n" />;
    case 'pro-time':
      return <ProTime jobs={jobs.data} employees={employees.data} timeEntries={timeEntries.data} onAdd={entry => timeEntries.add(entry)} onUpdate={(id, changes) => timeEntries.update(id, changes)} onRemove={id => timeEntries.remove(id)} data-id="element-1584" data-mp="3oky6g02yavnu" />;
    case 'pro-materials':
      return <ProMaterials materials={materials.data} onAdd={material => materials.add(material)} onUpdate={(id, changes) => materials.update(id, changes)} onRemove={id => materials.remove(id)} data-id="element-1585" data-mp="35tezji5t2xgj" />;
    case 'pro-invoices':
      return <ProInvoices invoices={invoices.data} company={settings} onUpdate={(id, changes) => invoices.update(id, changes)} data-id="element-1586" data-mp="g3v01jbx7ib8" />;
    case 'pro-employees':
      return <ProEmployees employees={employees.data} jobs={jobs.data} timeEntries={timeEntries.data} onOpenJob={openJob} data-id="element-1587" data-mp="2qr86r3wvomc0" />;
    case 'pro-statistics':
      return <ProStatistics leads={leads.data} quotes={quotes.data} jobs={jobs.data} invoices={invoices.data} employees={employees.data} timeEntries={timeEntries.data} vatRate={settings.vatRate} data-id="element-1588" data-mp="2zserxqah2lbl" />;
    case 'pro-settings':
      return <ProSettings company={settings} employees={employees.data} onSave={saveSettings} onReimportLegacy={data.reimportLegacy} data-id="element-1589" data-mp="3eidm7hywpcls" />;
    default:
      return null;
  }
}